import React, { useEffect, useRef, useState } from 'react';
import { AsYouType, parsePhoneNumberFromString } from 'libphonenumber-js';
import { COUNTRIES, DEFAULT_COUNTRY_CODE, findCountry, getFlagEmoji } from '../utils/countries';
import './PhoneNumberInput.css';

// Phone number input with a country code selector (flags + dial codes,
// defaulting to US) and live "(XXX) XXX-XXXX" formatting of the national
// number. Emits the full E.164 value via onChange.
const PhoneNumberInput = ({ value, onChange, placeholder, autoFocus }) => {
  const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE);
  const [nationalText, setNationalText] = useState('');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current || !value) return;
    const parsed = parsePhoneNumberFromString(value);
    if (parsed && parsed.country) {
      setCountryCode(parsed.country);
      setNationalText(new AsYouType(parsed.country).input(parsed.nationalNumber));
    }
    initializedRef.current = true;
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const country = findCountry(countryCode);

  const handleChangeNational = (e) => {
    const formatter = new AsYouType(countryCode);
    const formatted = formatter.input(e.target.value);
    setNationalText(formatted);
    onChange(formatter.getNumber()?.number || '');
  };

  const handleSelectCountry = (code) => {
    setCountryCode(code);
    setOpen(false);
    setSearch('');
    const digitsOnly = nationalText.replace(/\D/g, '');
    const formatter = new AsYouType(code);
    const formatted = formatter.input(digitsOnly);
    setNationalText(formatted);
    onChange(formatter.getNumber()?.number || '');
  };

  const filteredCountries = search.trim()
    ? COUNTRIES.filter(
        (c) =>
          c.name.toLowerCase().includes(search.trim().toLowerCase()) ||
          c.dialCode.includes(search.trim())
      )
    : COUNTRIES;

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div className="golocal-phone-input">
        <button
          type="button"
          className="golocal-phone-country-btn"
          onClick={() => setOpen((v) => !v)}
        >
          <span style={{ marginRight: 6 }}>{getFlagEmoji(country.code)}</span>
          <span>{country.dialCode}</span>
        </button>
        <input
          type="tel"
          className="golocal-phone-national-input"
          value={nationalText}
          onChange={handleChangeNational}
          placeholder={placeholder || '(555) 123-4567'}
          autoFocus={autoFocus}
          autoComplete="tel"
        />
      </div>

      {open && (
        <div className="golocal-country-dropdown">
          <input
            type="text"
            className="golocal-country-search"
            placeholder="Search countries"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <div className="golocal-country-list">
            {filteredCountries.map((c) => (
              <button
                type="button"
                key={c.code}
                className="golocal-country-option"
                onClick={() => handleSelectCountry(c.code)}
              >
                <span style={{ marginRight: 8 }}>{getFlagEmoji(c.code)}</span>
                <span style={{ flex: 1, textAlign: 'left' }}>{c.name}</span>
                <span style={{ color: '#94a3b8' }}>{c.dialCode}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PhoneNumberInput;
